import { useState } from "react";
import { toast } from "sonner";
import MembersEditor from "@/components/MembersEditor";
import QuestionField from "@/components/QuestionField";
import ActivityCounts from "@/components/ActivityCounts";
import ShirtOrder from "@/components/ShirtOrder";
import PaymentPlan from "@/components/PaymentPlan";
import { estimateDue } from "@/lib/format";

function Section({ n, title, children }) {
  return (
    <section className="card-warm">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-10 rounded-full bg-[#2B5338] text-white font-heading font-bold text-lg flex items-center justify-center">{n}</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-amber-900">{title}</h2>
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

export function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-lg font-semibold mb-2">{label}</span>
      {children}
      {hint && <span className="block text-stone-600 mt-1">{hint}</span>}
    </label>
  );
}

function ChoiceCard({ active, onClick, title, sub, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-pressed={active}
      className={`text-left min-h-[60px] p-4 rounded-2xl border-2 ${active ? "border-[#2B5338] bg-green-50" : "border-[#E6DBCF] bg-white hover:border-amber-400"}`}>
      <span className="block text-lg font-semibold">{active ? "✓ " : ""}{title}</span>
      {sub && <span className="block text-stone-600">{sub}</span>}
    </button>
  );
}

const EMPTY = { family_name: "", contact_name: "", email: "", phone: "", answers: {}, activity_signups: {}, shirt_orders: {}, team_ids: [], notes: "", pay_plan: "full", pay_plan_amount: null, pay_method: "" };

function groupBySection(list) {
  const out = [];
  list.forEach((q) => {
    const key = q.section || "A few questions";
    let g = out.find((x) => x.key === key);
    if (!g) out.push((g = { key, items: [] }));
    g.items.push(q);
  });
  return out;
}

export default function FamilyForm({ initial, data, onSubmit, submitLabel, busy, admin = false }) {
  const settings = data?.settings || {};
  const fees = settings.fees || [];
  const defaultFee = fees.find((x) => x.per !== "family")?.id || "";
  const [f, setF] = useState(() => ({ ...EMPTY, members: [{ name: "", age: "", fee_id: defaultFee, notes: "" }], ...(initial || {}) }));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const questions = data?.questions || [];
  const activities = data?.activities || [];
  const teams = data?.teams || [];
  const byPos = (pos) => questions.filter((q) => (q.position || "before") === pos);
  const qFields = (list) => list.map((q) => (
    <Field key={q.id} label={`${q.label}${q.required ? " *" : ""}`}>
      <QuestionField q={q} value={f.answers[q.id]} onChange={(v) => set("answers", { ...f.answers, [q.id]: v })} />
    </Field>
  ));
  const shirtSizes = settings.shirt_sizes || [];
  const showShirts = Number(settings.shirt_price) > 0 && shirtSizes.length > 0;
  const est = estimateDue(f, settings, activities);
  let n = 0;

  const submit = (e) => {
    e.preventDefault();
    const members = f.members.filter((m) => m.name.trim()).map((m) => ({ ...m, fee_id: m.fee_id || null }));
    if (!members.length) return toast.error("Please add at least one person's name.");
    const missing = questions.find((q) => q.required && !(Array.isArray(f.answers[q.id]) ? f.answers[q.id].length : f.answers[q.id]));
    if (missing) return toast.error(`Please answer: ${missing.label}`);
    const due = f.due_override === "" || f.due_override === undefined ? null : f.due_override;
    onSubmit({ ...f, members, ...(admin ? { due_override: due } : {}) });
  };

  const toggleTeam = (id) => set("team_ids", f.team_ids.includes(id) ? f.team_ids.filter((x) => x !== id) : [...f.team_ids, id]);

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="family-form">
      <Section n={++n} title="Your household">
        {qFields(byPos("household"))}
        <Field label="Household name"><input required data-testid="family-name-input" className="field" value={f.family_name} onChange={(e) => set("family_name", e.target.value)} /></Field>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Main contact person"><input required={!admin} data-testid="contact-name-input" className="field" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></Field>
          <Field label="Phone"><input data-testid="phone-input" type="tel" className="field" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        </div>
        <Field label="Email (optional)" hint="Add your email so we can send you a code to change your registration later.">
          <input data-testid="email-input" type="email" className="field" value={f.email || ""} onChange={(e) => set("email", e.target.value)} />
        </Field>
      </Section>

      <Section n={++n} title="Who's coming?">
        <p className="text-lg">Add everyone in your household who will attend, including keiki and kūpuna.</p>
        <MembersEditor members={f.members} fees={fees} onChange={(v) => set("members", v)} />
      </Section>

      {groupBySection(byPos("before")).map((g) => <Section key={g.key} n={++n} title={g.key}>{qFields(g.items)}</Section>)}

      {activities.length > 0 && (
        <Section n={++n} title="Activities">
          <ActivityCounts activities={activities} value={f.activity_signups || {}} onChange={(v) => set("activity_signups", v)} />
        </Section>
      )}

      {showShirts && (
        <Section n={++n} title="Reunion t-shirts">
          <ShirtOrder sizes={shirtSizes} price={Number(settings.shirt_price)} value={f.shirt_orders || {}} onChange={(v) => set("shirt_orders", v)} />
        </Section>
      )}

      {teams.length > 0 && (
        <Section n={++n} title="Help out">
          <p className="text-lg">Pick any teams you'd like to help with.</p>
          <div className="grid sm:grid-cols-2 gap-3">{teams.map((t) => <ChoiceCard key={t.id} testId={`team-option-${t.id}`} active={f.team_ids.includes(t.id)} onClick={() => toggleTeam(t.id)} title={t.name} sub={t.description} />)}</div>
        </Section>
      )}

      {groupBySection(byPos("after")).map((g) => <Section key={g.key} n={++n} title={g.key}>{qFields(g.items)}</Section>)}

      <Section n={++n} title="Payment">
        <PaymentPlan f={f} set={set} est={est} settings={settings} />
      </Section>

      <div className="card-warm !bg-[#FDF6ED] space-y-5">
        <Field label="Anything else we should know? (optional)"><textarea data-testid="family-notes-input" className="field min-h-[90px]" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
        {admin && (
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Amount due override ($)" hint="Leave blank to use the fee calculation."><input data-testid="due-override-input" className="field" inputMode="decimal" value={f.due_override ?? ""} onChange={(e) => set("due_override", e.target.value === "" ? null : Number(e.target.value))} /></Field>
            <label className="flex items-center gap-3 text-lg font-semibold mt-8"><input type="checkbox" data-testid="checked-in-checkbox" className="w-6 h-6 accent-[#2B5338]" checked={!!f.checked_in} onChange={(e) => set("checked_in", e.target.checked)} />Checked in</label>
          </div>
        )}
        <button type="submit" disabled={busy} data-testid="registration-form-submit" className="btn-primary w-full sm:w-auto">{busy ? "Saving…" : submitLabel}</button>
      </div>
    </form>
  );
}
